'use client';
import { useT } from '@/lib/useT';
import { useState } from 'react';
import { api } from '@/lib/api';
import toast from 'react-hot-toast';
import Link from 'next/link';
import { Mail, ArrowLeft, MailCheck } from 'lucide-react';
import { Button, Input } from '@/components/ui';
import { AuthShell, AuthCard, AuthField, AuthLinks, AuthLink } from '@/components/shell/AuthShell';

export default function ForgotPasswordPage() {
  const t = useT();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
      toast.success(t('forgot_link_sent'));
    } catch (err: any) {
      toast.error(err.message || t('afp_send_error'));
    } finally {
      setLoading(false);
    }
  };

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  return (
    <AuthShell
      kicker={label('forgot_kicker', 'Account recovery')}
      headline={label('forgot_headline', 'Locked out? We’ll send you a link.')}
      lede={label('forgot_lede', 'Enter the email you sign in with. If an account exists, a reset link arrives within a minute.')}
    >
      <AuthCard
        title={label('forgot_title', 'Reset your password')}
        subtitle={sent ? undefined : label('forgot_subtitle', 'We will email you a link to set a new one.')}
      >
        {sent ? (
          <div className="flex flex-col items-center gap-2.5 py-4 text-center">
            <MailCheck size={20} className="text-[var(--success)]" />
            <p className="text-[13px] font-medium text-[var(--text-primary)]">
              {label('forgot_link_sent', 'Check your inbox')}
            </p>
            <p className="text-[12.5px] text-[var(--text-muted)] max-w-[36ch]">
              {label('forgot_sent_hint', 'If an account exists for this address, the reset link is on its way. It expires in one hour.')}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <AuthField label={label('login_email', 'Email address')} icon={Mail}>
              <Input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                required autoComplete="email" placeholder="mail@unternehmen.de"
              />
            </AuthField>
            <Button type="submit" variant="primary" disabled={loading} className="w-full h-11 text-[14px]">
              {loading ? label('forgot_sending', 'Sending…') : label('forgot_submit', 'Send reset link')}
            </Button>
          </form>
        )}
      </AuthCard>

      <AuthLinks>
        <AuthLink href="/login">
          <span className="inline-flex items-center gap-1.5"><ArrowLeft size={13} /> {label('forgot_back', 'Back to sign in')}</span>
        </AuthLink>
      </AuthLinks>
    </AuthShell>
  );
}
